import { useNavigate, useLocation } from 'react-router-dom'

/**
 * `next/navigation`, implemented over react-router.
 *
 * WHY A SHIM RATHER THAN EDITING 16 FILES. The surface actually used by this
 * app is three exports and three router methods — `useRouter().push/replace/
 * refresh`, `usePathname`, `redirect`. Rewriting every call site would touch
 * sixteen files across `app/`, `components/` and `hooks/` for no behavioural
 * gain, and it would break the Next build immediately, which this branch still
 * needs while the two shells coexist. A twenty-line adapter keeps both alive
 * and keeps the diff readable.
 *
 * `vite.config.ts` aliases `next/navigation` here. Nothing imports it directly.
 */

export function useRouter() {
  const navigate = useNavigate()
  return {
    push: (href: string) => navigate(href),
    replace: (href: string) => navigate(href, { replace: true }),
    back: () => navigate(-1),
    forward: () => navigate(1),
    /**
     * Next's `refresh()` re-fetched the server components for the current
     * route. There are none: every page is a client component fed by the
     * Zustand store over Supabase. The honest equivalent is "nothing to do" —
     * the three call sites all pair it with a `push()` after signing in or out,
     * where the store is re-hydrated by StoreHydrator on the next mount anyway.
     *
     * It is kept as a no-op rather than removed so those call sites do not have
     * to change, and so the day someone adds server rendering back they find
     * this note instead of silence.
     */
    refresh: () => {},
    prefetch: () => {},
  }
}

export function usePathname(): string {
  return useLocation().pathname
}

/**
 * Next's `redirect()` threw a control-flow signal that the framework caught
 * during rendering. Outside Next there is nothing to catch it, so this does the
 * navigation directly. Only one caller exists (`app/page.tsx`, the `/` →
 * `/warehouse` redirect), and the router handles that route itself now — this
 * exists so the import keeps resolving.
 */
export function redirect(href: string): never {
  window.location.replace(href)
  // `never` matches Next's signature: callers may rely on code after it being
  // unreachable. The throw also stops the current render, as Next's did.
  throw new Error(`redirect(${href})`)
}

export function useSearchParams(): URLSearchParams {
  return new URLSearchParams(useLocation().search)
}
